import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';
import { get } from 'lodash';
import { AlertTriangle } from 'react-feather';
import regex from 'constants/regex';
import * as commentaryActions from 'actions/editor/commentary';
import * as integrationsActions from 'actions/editor/integrations';
import elementsActions from 'actions/editor/elements';
import { isLessonEmpty } from 'selectors/editor';
import ImageLibraryAddDialog from 'components/shared/ImageLibraryAddDialog';
import EditorWindowLocation from './window_location';
import SectionsList from './sections';
import ContentHeader from './content/header';
import ContentElements from './content';
import EditorBlankSlate from './blankslate';
import ErrorAlert from './error_alert';
import EditorHeaderV2 from './HeaderV2';
import Commentary from './collaboration/Commentary';
import DNDScrollZone from '../shared/DNDScrollZone';

class Editor extends Component {
  constructor(props) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }

  editorRef = React.createRef();

  componentDidMount() {
    if (Editor.shouldAutoFetchComments) {
      const { hash } = window.location;
      const [, elementId, threadId] = regex.COMMENT_URL_HASH.exec(hash);
      this.props.autoFetchCommentary(+elementId, +threadId);
    }
    this.props.fetchIntegration('gong');
    document.addEventListener('click', this.handleClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClick);
  }

  static get shouldAutoFetchComments() {
    return regex.COMMENT_URL_HASH.test(window.location.hash);
  }

  handleClick({ target }) {
    const { hideCommentary, isCommenting } = this.props;
    if (isCommenting && target === this.sidebar) hideCommentary();
  }

  renderContent() {
    if (this.props.emptyLesson) return <EditorBlankSlate />;

    const {
      currentSection,
      lessonTitle,
      sectionTitle,
      isFetching,
      showInvalidSectionRibbon,
      templateActive,
    } = this.props;
    const { id } = currentSection;
    const styleName = classNames('Editor-main', {
      'Editor-main--loading': isFetching,
      'Editor-main--invalid': showInvalidSectionRibbon,
    });

    return (
      <div className={styleName}>
        {showInvalidSectionRibbon && (
          <div className="alert alert-warning w-fill c-black border-none">
            <div className="pl-8p d-f">
              <AlertTriangle size={18} className="mr-8" />
              <span>
                {t(
                  'drafts.publishing.lesson_confirmation.required_fields_incomplete',
                )}
              </span>
            </div>
          </div>
        )}
        <ContentHeader {...{ lessonTitle, sectionTitle, id }} key={id} />
        <ContentElements
          section={currentSection}
          templateActive={templateActive}
        />
      </div>
    );
  }

  render() {
    const { isCollapsed, isCommenting } = this.props;
    const containerClassName = classNames('LessonBuilder', {
      'is-commenting': isCommenting,
    });
    const editorClassName = classNames('Editor', {
      'Editor--collapsed': isCollapsed,
    });

    return (
      <div className={containerClassName}>
        <EditorWindowLocation />
        <ErrorAlert />
        <DNDScrollZone willReceiveDraggable={this.props.isDraggingElement} />
        <div className="LessonBuilder-editor">
          <EditorHeaderV2 />
          <div className={editorClassName} ref={this.editorRef}>
            <div
              className="Editor-side"
              ref={n => {
                this.sidebar = n;
              }}
            >
              <SectionsList />
            </div>
            {this.renderContent()}
          </div>
        </div>
        {this.props.isCommenting && (
          <div className="LessonBuilder-commentary">
            <Commentary editorRef={this.editorRef} />
          </div>
        )}
        <ImageLibraryAddDialog
          canManageContentLibrary={
            this.props.features.can_manage_content_library
          }
          image={this.props.imageLibraryDialog.image}
          visible={this.props.imageLibraryDialog.visible}
          toggleVisible={this.props.toggleAddToImageLibrary}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ editor, meta }) => ({
  currentSection: editor.currentSection,
  lessonTitle: editor.lessonTitle,
  sectionTitle: get(editor.currentSection, 'title'),
  sections: editor.sections,
  emptyLesson: isLessonEmpty(editor),
  isFetching: editor.isFetching,
  isCollapsed: editor.commentary.visible,
  isCommenting: editor.commentary.visible,
  isDraggingElement: editor.isDraggingElement,
  imageLibraryDialog: editor.imageLibraryDialog,
  showInvalidSectionRibbon:
    !!editor.sectionsWithInvalidElements.length &&
    editor.sectionsWithInvalidElements.indexOf(editor.currentSection.id) !== -1,
  features: meta.features,
  templateActive: editor.templateActive,
});

const mapDispatchToProps = dispatch => ({
  hideCommentary() {
    dispatch(commentaryActions.hideCommentary());
  },
  autoFetchCommentary(elementId, threadId) {
    return dispatch(
      commentaryActions.autoFetchCommentary(elementId, threadId, true),
    );
  },
  toggleAddToImageLibrary(image) {
    return dispatch(elementsActions.toggleAddToImageLibrary(image));
  },
  fetchIntegration(appIndicator) {
    return dispatch(integrationsActions.fetchIntegration(appIndicator));
  },
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(Editor);
